/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { useEffect, useState } from "react";
import { registerUser, resendVerification } from "../api/api";
import { getActiveTrialSubscriptions } from "../api/subscriptions";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PaxiaLogo from "../branding/logo";

const PASSWORD_MIN_LENGTH = 8;
const EXPERIENCE_OPTIONS = [
  { value: "BEGINNER", label: "Beginner — new to trading" },
  { value: "INTERMEDIATE", label: "Intermediate — some live experience" },
  { value: "ADVANCED", label: "Advanced — trade actively" },
  { value: "PROFESSIONAL", label: "Professional — trade for a living" },
];

const getPasswordStrength = (password) => {
  if (!password) return { label: "", color: "", width: "0%" };
  let score = 0;
  if (password.length >= PASSWORD_MIN_LENGTH) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 1) return { label: "Weak", color: "bg-red-500", width: "20%" };
  if (score === 2) return { label: "Fair", color: "bg-orange-400", width: "40%" };
  if (score === 3) return { label: "Good", color: "bg-yellow-400", width: "60%" };
  if (score === 4) return { label: "Strong", color: "bg-green-400", width: "80%" };
  return { label: "Very Strong", color: "bg-green-500", width: "100%" };
};

const Register = () => {
  const [searchParams] = useSearchParams();
  const requestedPlanRaw = (searchParams.get("plan") || "trial").toLowerCase();
  const requestedPlan = requestedPlanRaw.replace(/[^a-z0-9_-]/g, "") || "trial";
  const [formData, setFormData] = useState({
    password: "",
    password_confirm: "",
    email: "",
    first_name: "",
    last_name: "",
    trading_experience: "",
    accepted_terms: false,
    marketing_opt_in: false,
    plan: requestedPlan,
  });
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [trialOffer, setTrialOffer] = useState(null);
  const [trialOfferLoading, setTrialOfferLoading] = useState(requestedPlan === "trial");
  const [resendEmail, setResendEmail] = useState("");
  const [resendStatus, setResendStatus] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    if (requestedPlan !== "trial") {
      setTrialOfferLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setTrialOfferLoading(true);
    getActiveTrialSubscriptions()
      .then((offers) => {
        if (cancelled) return;
        const offer = Array.isArray(offers) && offers.length > 0 ? offers[0] : null;
        setTrialOffer(offer);
        if (offer?.code) {
          setFormData((prev) => (
            prev.plan === "trial" ? { ...prev, plan: offer.code } : prev
          ));
        }
      })
      .catch(() => {
        if (!cancelled) setTrialOffer(null);
      })
      .finally(() => {
        if (!cancelled) setTrialOfferLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [requestedPlan]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const passwordStrength = getPasswordStrength(formData.password);
  const passwordsMatch =
    formData.password_confirm.length > 0 &&
    formData.password === formData.password_confirm;
  const passwordsMismatch =
    formData.password_confirm.length > 0 &&
    formData.password !== formData.password_confirm;

  const handleRegister = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccessMessage("");

    // Client-side validation
    const clientErrors = {};
    if (!formData.first_name.trim()) {
      clientErrors.first_name = ["First name is required."];
    }
    if (!formData.last_name.trim()) {
      clientErrors.last_name = ["Last name is required."];
    }
    if (formData.password.length < PASSWORD_MIN_LENGTH) {
      clientErrors.password = [`Password must be at least ${PASSWORD_MIN_LENGTH} characters.`];
    }
    if (formData.password !== formData.password_confirm) {
      clientErrors.password_confirm = ["Passwords do not match."];
    }
    if (!formData.accepted_terms) {
      clientErrors.accepted_terms = ["You must accept the terms of service."];
    }
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }

    setLoading(true);
    try {
      // Backend register endpoint accepts: email/password/names/terms/marketing_opt_in/plan.
      // trading_experience is captured here for UX continuity but stored later
      // (after login) as part of the onboarding wizard.
      const { trading_experience, ...registerPayload } = formData;
      if (trading_experience) {
        try {
          sessionStorage.setItem("paxia:onboarding:experience", trading_experience);
        } catch { /* ignore quota / private mode errors */ }
      }
      const result = await registerUser(registerPayload);
      const trialStarted = Boolean(result?.trial_started);
      const periodDays = result?.trial_period_days || trialOffer?.trial_period_days;
      const trialName = result?.trial_tier_name || trialOffer?.name || "trial";
      const trialLabel = periodDays
        ? `${periodDays}-day ${trialName} trial`
        : `${trialName} trial`;
      if (result?.verification_email_failed) {
        setSuccessMessage(
          trialStarted
            ? `Your account is ready and your free ${trialLabel} has started! We couldn't send the verification email — please use the button below to resend it.`
            : "Your account is ready. We couldn't send the verification email — please use the button below to resend it."
        );
        setResendEmail(formData.email);
      } else {
        setSuccessMessage(
          trialStarted
            ? `Your account is ready and your free ${trialLabel} has started! We've sent a verification link to your email — confirm your address to unlock all features.`
            : "Your account is ready. We've sent a verification link to your email — confirm your address to unlock all features."
        );
        setResendEmail(formData.email);
      }
    } catch (err) {
      // err.data holds raw field-level validation errors from the API (e.g. {email: ["..."]}).
      const fieldErrors = err?.data && typeof err.data === "object" ? err.data : null;
      if (fieldErrors) {
        setErrors(fieldErrors);
      } else if (err?.message) {
        setErrors({ general: [err.message] });
      } else {
        setErrors({ general: ["Registration failed. Please try again."] });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-[#0F1826] font-inter text-white overflow-hidden px-4 sm:px-6 py-6">
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-[#0F1826]/90 to-[#0F1826]/90"></div>
      <div className="relative z-20 bg-[#1A2539]/80 backdrop-blur-md p-5 sm:p-8 rounded-xl shadow-lg w-full max-w-[340px] sm:max-w-[420px]">
        <div className="flex flex-col items-center mb-4 space-y-3">
          <img
            src={PaxiaLogo}
            alt="paxia.ai"
            className="h-14 sm:h-16 w-auto object-contain bg-transparent select-none"
          />
          <h1 className="text-2xl font-bold tracking-tight">Create Account</h1>
          <p className="text-sm text-gray-400">Join paxia.ai</p>
        </div>

        {!successMessage && requestedPlan === "trial" && (
          <div className="mb-4 rounded-lg border border-[#4FA9A1]/40 bg-[#4FA9A1]/10 px-3 py-2 text-xs text-[#A6E0DB]">
            {trialOfferLoading ? (
              "Loading current trial availability..."
            ) : trialOffer ? (
              <>
                <span className="font-semibold text-[#4FA9A1]">
                  Free {trialOffer.trial_period_days}-day {trialOffer.name} trial.
                </span>{" "}
                Full access. No credit card required.
              </>
            ) : (
              "Trial signup is not currently available. You can still create an account and choose a plan later."
            )}
          </div>
        )}
        {!successMessage && requestedPlan !== "trial" && (
          <div className="mb-4 rounded-lg border border-[#2A3954] bg-[#0F1826]/60 px-3 py-2 text-xs text-gray-300">
            Selected plan:{" "}
            <span className="font-semibold capitalize text-[#4FA9A1]">{requestedPlan}</span>
          </div>
        )}

        {successMessage && (
          <div className="bg-green-500/20 border border-green-500 text-green-300 p-3 rounded-lg text-center mb-4">
            <p>{successMessage}</p>
            <div className="flex flex-col items-center gap-2 mt-3">
              <button
                onClick={() => navigate("/login")}
                className="bg-[#4FA9A1] hover:bg-[#3e938b] transition-colors text-[#0F1826] font-semibold py-1.5 px-4 rounded-lg text-sm"
              >
                Go to Login
              </button>
              {resendEmail && (
                <button
                  type="button"
                  onClick={async () => {
                    setResendStatus("sending");
                    try {
                      await resendVerification(resendEmail);
                      setResendStatus("sent");
                    } catch {
                      setResendStatus("error");
                    }
                  }}
                  disabled={resendStatus === "sending" || resendStatus === "sent"}
                  className="text-[#4FA9A1] hover:underline text-xs disabled:opacity-50"
                >
                  {resendStatus === "sending"
                    ? "Sending..."
                    : resendStatus === "sent"
                    ? "Verification email resent!"
                    : resendStatus === "error"
                    ? "Failed — click to retry"
                    : "Didn't get the email? Resend"}
                </button>
              )}
            </div>
          </div>
        )}

        {errors.general && (
          <p className="text-red-400 text-center mt-2 text-sm">{errors.general.join(" ")}</p>
        )}

        {!successMessage && (
        <form onSubmit={handleRegister} className="mt-4 space-y-4">
          <div>
            <input
              type="email"
              name="email"
              placeholder="Email"
              className="w-full p-3 rounded-lg bg-[#0F1826]/60 border border-[#2A3954] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4FA9A1]"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="email"
            />
            {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email.join(" ")}</p>}
          </div>

          <div>
            <input
              type="password"
              name="password"
              placeholder="Password"
              className="w-full p-3 rounded-lg bg-[#0F1826]/60 border border-[#2A3954] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4FA9A1]"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={PASSWORD_MIN_LENGTH}
              autoComplete="new-password"
            />
            {formData.password && (
              <div className="mt-1.5">
                <div className="w-full h-1.5 bg-[#0F1826]/60 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${passwordStrength.color} transition-all duration-300`}
                    style={{ width: passwordStrength.width }}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-0.5">{passwordStrength.label}</p>
              </div>
            )}
            {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password.join(" ")}</p>}
          </div>

          <div>
            <input
              type="password"
              name="password_confirm"
              placeholder="Confirm Password"
              className={`w-full p-3 rounded-lg bg-[#0F1826]/60 border text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4FA9A1] ${
                passwordsMismatch
                  ? "border-red-500"
                  : passwordsMatch
                  ? "border-green-500"
                  : "border-[#2A3954]"
              }`}
              value={formData.password_confirm}
              onChange={handleChange}
              required
              autoComplete="new-password"
            />
            {passwordsMatch && (
              <p className="text-green-400 text-xs mt-1">Passwords match</p>
            )}
            {errors.password_confirm && (
              <p className="text-red-400 text-xs mt-1">{errors.password_confirm.join(" ")}</p>
            )}
          </div>

          <div className="flex gap-3">
            <div className="w-1/2">
              <input
                type="text"
                name="first_name"
                placeholder="First Name"
                className="w-full p-3 rounded-lg bg-[#0F1826]/60 border border-[#2A3954] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4FA9A1]"
                value={formData.first_name}
                onChange={handleChange}
                required
                autoComplete="given-name"
              />
              {errors.first_name && <p className="text-red-400 text-xs mt-1">{errors.first_name.join(" ")}</p>}
            </div>
            <div className="w-1/2">
              <input
                type="text"
                name="last_name"
                placeholder="Last Name"
                className="w-full p-3 rounded-lg bg-[#0F1826]/60 border border-[#2A3954] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4FA9A1]"
                value={formData.last_name}
                onChange={handleChange}
                required
                autoComplete="family-name"
              />
              {errors.last_name && <p className="text-red-400 text-xs mt-1">{errors.last_name.join(" ")}</p>}
            </div>
          </div>
          <div>
            <select
              name="trading_experience"
              value={formData.trading_experience}
              onChange={handleChange}
              className="w-full p-3 rounded-lg bg-[#0F1826]/60 border border-[#2A3954] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4FA9A1]"
            >
              <option value="">Trading experience (optional)</option>
              {EXPERIENCE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              name="marketing_opt_in"
              checked={formData.marketing_opt_in}
              onChange={handleChange}
              className="mt-1 h-4 w-4 rounded border-[#2A3954] bg-[#0F1826]/60 text-[#4FA9A1] focus:ring-[#4FA9A1] accent-[#4FA9A1]"
            />
            <span className="text-xs text-gray-400">
              Send me product updates and trading insights (optional).
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              name="accepted_terms"
              checked={formData.accepted_terms}
              onChange={handleChange}
              className="mt-1 h-4 w-4 rounded border-[#2A3954] bg-[#0F1826]/60 text-[#4FA9A1] focus:ring-[#4FA9A1] accent-[#4FA9A1]"
            />
            <span className="text-xs text-gray-400">
              I agree to the{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#4FA9A1] hover:underline">
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#4FA9A1] hover:underline">
                Privacy Policy
              </a>
            </span>
          </label>
          {errors.accepted_terms && (
            <p className="text-red-400 text-xs -mt-2">{errors.accepted_terms.join(" ")}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#4FA9A1] hover:bg-[#3e938b] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-[#0F1826] font-semibold py-2 rounded-lg flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Creating Account...
              </>
            ) : (
              "Register"
            )}
          </button>
        </form>
        )}
        <p className="text-center text-sm text-gray-400 mt-4">
          Already have an account?{" "}
          <Link to="/login" className="text-[#4FA9A1] hover:underline">
            Login
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
